// Global Types
import type {
  $Children,
  $GeoLocation,
} from 'common.types';

// Global Components
import * as React from 'react';
import {
  GoogleMap,
  useJsApiLoader,
} from '@react-google-maps/api';

// Helpers
import config from '../../../config';

// Types
type $OptionalProps = {
  controls?: boolean;
  draggable?: boolean;
};

type $Props = $OptionalProps & {
  center: $GeoLocation;
  children: $Children;
  height: number | string;
  zoom: number;
};

function MapView({
  center,
  children,
  controls,
  draggable,
  height,
  zoom,
}: $Props) {
  const containerStyle = {
    height: typeof height === 'string' ? height : `${height}px`,
    width: '100%',
  };

  const {
    isLoaded,
  } = useJsApiLoader({
    googleMapsApiKey: config.GOOGLE_MAPS_API_KEY,
    id: 'google-map-script',
  });

  return isLoaded ? (
    <GoogleMap
      center={center}
      mapContainerStyle={containerStyle}
      options={{
        disableDefaultUI: !controls,
        draggable,
        styles: [{
          elementType: 'labels.text.fill',
          featureType: 'administrative',
          stylers: [{
            color: '#444444',
          }],
        }, {
          elementType: 'all',
          featureType: 'landscape',
          stylers: [{
            color: '#f2f2f2',
          }],
        }, {
          elementType: 'all',
          featureType: 'poi',
          stylers: [{
            visibility: 'off',
          }],
        }, {
          elementType: 'all',
          featureType: 'road',
          stylers: [{
            saturation: -100,
          }, {
            lightness: 45,
          }],
        }, {
          elementType: 'all',
          featureType: 'road.highway',
          stylers: [{
            visibility: 'simplified',
          }],
        }, {
          elementType: 'labels.icon',
          featureType: 'road.arterial',
          stylers: [{
            visibility: 'off',
          }],
        }, {
          elementType: 'all',
          featureType: 'transit',
          stylers: [{
            visibility: 'off',
          }],
        }, {
          elementType: 'all',
          featureType: 'water',
          stylers: [{
            color: '#AAD5FF',
          }, {
            visibility: 'on',
          }],
        }],
        zoomControl: true,
      }}
      zoom={zoom}
    >
      {children}
    </GoogleMap>
  ) : <div />;
}

MapView.defaultProps = {
  controls: undefined,
  draggable: undefined,
};

export default MapView;
